import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	createHookWithAssistanceService,
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { isDefaultRovoChat } from '@atlassian/conversation-assistant-utils/default-agent';

import type { AgentIconType } from '@confluence/agent-user-dual-avatar/entry-points/agentIconType.ts';
import { getAgentBrand } from '@confluence/agent-user-dual-avatar/entry-points/getAgentBrand.ts';
import { fg } from '@confluence/feature-gating';

import { fetchPresenceAGGUsers } from '../page-presence/fetchPresenceAGGUsers.ts';

import type { AgentWithPresence } from './AgentPresenceTypes.ts';

// Shared by Canvas and by page/editor when confluence_agent_presence_shared_agent_code is enabled.
export type AgentIdentifier = {
	agentId: string;
	agentType?: string;
};

type UseAgentIdentifiersWithAgentEditsProps = {
	agentIdentifiers: AgentIdentifier[];
	fallbackAgentName: string;
	agentNames: AgentPresenceAgentNames;
} & AssistanceServiceInjectedParams;

type AssistanceService =
	AssistanceServiceInjectedParams['assistanceServiceInjectedParams']['assistanceService'];

type IdentityUser = Awaited<ReturnType<typeof fetchPresenceAGGUsers>>[number];

type AgentPresenceAssistanceServiceParams = AssistanceServiceInputParam['assistanceServiceParams'];

export type AgentPresenceAgentNames = Record<
	| 'chatgpt'
	| 'claude'
	| 'codex'
	| 'cursor'
	| 'dia'
	| 'figma'
	| 'github_copilot'
	| 'grok'
	| 'hermes'
	| 'kiro'
	| 'lovable'
	| 'open_code'
	| 'replit'
	| 'rovo'
	| 'vscode',
	string
>;

const i18n = defineMessages({
	externalAgentName: {
		id: 'team-presence.external-agent.name',
		defaultMessage: 'External agent',
		description: 'Display name for an external agent whose details could not be resolved',
	},
	rovoAgentName: {
		id: 'team-presence.rovo-agent.name',
		defaultMessage: 'Rovo',
		description: 'Display name for the Rovo agent in presence labels',
	},
	claudeAgentName: {
		id: 'team-presence.claude-agent.name',
		defaultMessage: 'Claude',
		description: 'Display name for the Claude agent in presence labels',
	},
	codexAgentName: {
		id: 'team-presence.codex-agent.name',
		defaultMessage: 'Codex',
		description: 'Display name for the Codex agent in presence labels',
	},
	cursorAgentName: {
		id: 'team-presence.cursor-agent.name',
		defaultMessage: 'Cursor',
		description: 'Display name for the Cursor agent in presence labels',
	},
	chatgptAgentName: {
		id: 'team-presence.chatgpt-agent.name',
		defaultMessage: 'ChatGPT',
		description: 'Display name for the ChatGPT agent in presence labels',
	},
	diaAgentName: {
		id: 'team-presence.dia-agent.name',
		defaultMessage: 'Dia',
		description: 'Display name for the Dia agent in presence labels',
	},
	figmaAgentName: {
		id: 'team-presence.figma-agent.name',
		defaultMessage: 'Figma',
		description: 'Display name for the Figma agent in presence labels',
	},
	githubCopilotAgentName: {
		id: 'team-presence.github-copilot-agent.name',
		defaultMessage: 'GitHub Copilot',
		description: 'Display name for the GitHub Copilot agent in presence labels',
	},
	grokAgentName: {
		id: 'team-presence.grok-agent.name',
		defaultMessage: 'Grok',
		description: 'Display name for the Grok agent in presence labels',
	},
	hermesAgentName: {
		id: 'team-presence.hermes-agent.name',
		defaultMessage: 'Hermes',
		description: 'Display name for the Hermes agent in presence labels',
	},
	kiroAgentName: {
		id: 'team-presence.kiro-agent.name',
		defaultMessage: 'Kiro',
		description: 'Display name for the Kiro agent in presence labels',
	},
	lovableAgentName: {
		id: 'team-presence.lovable-agent.name',
		defaultMessage: 'Lovable',
		description: 'Display name for the Lovable agent in presence labels',
	},
	openCodeAgentName: {
		id: 'team-presence.open-code-agent.name',
		defaultMessage: 'Open Code',
		description: 'Display name for the Open Code agent in presence labels',
	},
	replitAgentName: {
		id: 'team-presence.replit-agent.name',
		defaultMessage: 'Replit',
		description: 'Display name for the Replit agent in presence labels',
	},
	vscodeAgentName: {
		id: 'team-presence.vscode-agent.name',
		defaultMessage: 'Visual Studio Code',
		description: 'Display name for the Visual Studio Code agent in presence labels',
	},
});

export const useAgentPresenceResolverMessages = (): {
	agentNames: AgentPresenceAgentNames;
	fallbackAgentName: string;
} => {
	const { formatMessage } = useIntl();

	return useMemo(
		() => ({
			agentNames: {
				chatgpt: formatMessage(i18n.chatgptAgentName),
				claude: formatMessage(i18n.claudeAgentName),
				codex: formatMessage(i18n.codexAgentName),
				cursor: formatMessage(i18n.cursorAgentName),
				dia: formatMessage(i18n.diaAgentName),
				figma: formatMessage(i18n.figmaAgentName),
				github_copilot: formatMessage(i18n.githubCopilotAgentName),
				grok: formatMessage(i18n.grokAgentName),
				hermes: formatMessage(i18n.hermesAgentName),
				kiro: formatMessage(i18n.kiroAgentName),
				lovable: formatMessage(i18n.lovableAgentName),
				open_code: formatMessage(i18n.openCodeAgentName),
				replit: formatMessage(i18n.replitAgentName),
				rovo: formatMessage(i18n.rovoAgentName),
				vscode: formatMessage(i18n.vscodeAgentName),
			},
			fallbackAgentName: formatMessage(i18n.externalAgentName),
		}),
		[formatMessage],
	);
};

export const buildAgentPresenceAssistanceServiceParams = (
	cloudId: string,
): AgentPresenceAssistanceServiceParams => ({
	siteId: cloudId,
	product: 'confluence' as const,
	experienceId: 'ai-mate' as const,
	touchpointSource: 'aiAgentPresence' as const,
});

const createFallbackAgent = (agentId: string, name: string): AgentWithPresence => ({
	id: agentId,
	identity_account_id: null,
	named_id: agentId,
	name,
	description: null,
	creator_type: 'CUSTOMER',
	available_plugins: {},
	is_default: false,
	actor_type: 'AGENT',
	user_defined_conversation_starters: null,
	favourite: false,
	deactivated: false,
	isExternalAgentFallback: true,
});

const hasConcreteAgentId = ({ agentId, agentType }: AgentIdentifier): boolean =>
	Boolean(agentId) && agentId !== agentType;

const createIdentityAgent = (
	identifier: AgentIdentifier,
	user: IdentityUser,
	fallbackAgentName: string,
): AgentWithPresence => ({
	...createFallbackAgent(identifier.agentId, fallbackAgentName),
	agentType: identifier.agentType,
	avatarUrl: user.avatar,
	id: identifier.agentId,
	identity_account_id: user.userId,
	isExternalAgentFallback: false,
	named_id: user.userId,
	name: user.name,
});

const createTypedFallbackAgent = (
	identifier: AgentIdentifier,
	fallbackAgentName: string,
	agentIcon?: AgentIconType,
): AgentWithPresence => ({
	...createFallbackAgent(identifier.agentId, fallbackAgentName),
	agentType: identifier.agentType,
	...(agentIcon ? { agentIcon } : {}),
});

const resolveStudioAgent = async (
	identifier: AgentIdentifier,
	assistanceService: AssistanceService,
	fallbackAgentName: string,
	rovoAgentName: string,
): Promise<AgentWithPresence> => {
	try {
		const agent = await assistanceService.getAgentDetailsByIdentityAccountId(identifier.agentId);

		if (
			isDefaultRovoChat({
				agentId: agent.id,
				agentNamedId: agent.named_id,
			})
		) {
			return createTypedFallbackAgent(identifier, rovoAgentName, 'rovo');
		}

		const agentBrand =
			agent.creator_type === 'OOTB' ? getAgentBrand(agent.name.replace(/ Agent$/, '')) : undefined;

		return {
			...agent,
			agentType: identifier.agentType,
			...(agentBrand && { agentIcon: agentBrand.agentIcon }),
		};
	} catch {
		return createTypedFallbackAgent(identifier, fallbackAgentName);
	}
};

const resolveStudioAgentById = async (
	identifier: AgentIdentifier,
	assistanceService: AssistanceService,
	fallbackAgentName: string,
): Promise<AgentWithPresence> => {
	try {
		return {
			...(await assistanceService.getAgentDetails(identifier.agentId)),
			agentType: identifier.agentType,
		};
	} catch {
		return createTypedFallbackAgent(identifier, fallbackAgentName);
	}
};

const resolveIdentityAgent = async (
	identifier: AgentIdentifier,
	identityUsersByIdPromise: Promise<Map<string, IdentityUser>>,
	fallbackAgentName: string,
): Promise<AgentWithPresence> => {
	const user = (await identityUsersByIdPromise).get(identifier.agentId);

	return user
		? createIdentityAgent(identifier, user, fallbackAgentName)
		: createTypedFallbackAgent(identifier, fallbackAgentName);
};

const resolveAgentIdentifier = (
	identifier: AgentIdentifier,
	assistanceService: AssistanceService,
	identityUsersByIdPromise: Promise<Map<string, IdentityUser>>,
	fallbackAgentName: string,
	agentNames: UseAgentIdentifiersWithAgentEditsProps['agentNames'],
): Promise<AgentWithPresence> | AgentWithPresence => {
	switch (identifier.agentType) {
		case 'claude':
			return createTypedFallbackAgent(identifier, agentNames.claude, 'claude');
		case 'codex':
			return createTypedFallbackAgent(identifier, agentNames.codex, 'codex');
		case 'cursor':
			return createTypedFallbackAgent(identifier, agentNames.cursor, 'cursor');
		case 'chatgpt':
			return createTypedFallbackAgent(identifier, agentNames.chatgpt, 'chatgpt');
		case 'dia':
			return createTypedFallbackAgent(identifier, agentNames.dia, 'dia');
		case 'figma':
			return createTypedFallbackAgent(identifier, agentNames.figma, 'figma');
		case 'lovable':
			return createTypedFallbackAgent(identifier, agentNames.lovable, 'lovable');
		case 'replit':
			return createTypedFallbackAgent(identifier, agentNames.replit, 'replit');
		case 'grok':
			return createTypedFallbackAgent(identifier, agentNames.grok, 'grok');
		case 'github_copilot':
			return createTypedFallbackAgent(identifier, agentNames.github_copilot, 'github-copilot');
		case 'vscode':
			return createTypedFallbackAgent(identifier, agentNames.vscode, 'vscode');
		case 'open_code':
			return createTypedFallbackAgent(identifier, agentNames.open_code, 'open-code');
		case 'hermes':
			return createTypedFallbackAgent(identifier, agentNames.hermes, 'hermes');
		case 'kiro':
			return createTypedFallbackAgent(identifier, agentNames.kiro, 'kiro');
		case 'convo-ai':
			if (!hasConcreteAgentId(identifier)) {
				return createTypedFallbackAgent(identifier, agentNames.rovo, 'rovo');
			}
			return resolveStudioAgent(identifier, assistanceService, fallbackAgentName, agentNames.rovo);
		case 'mcp':
			return resolveIdentityAgent(identifier, identityUsersByIdPromise, fallbackAgentName);
		case 'twg':
			return createTypedFallbackAgent(identifier, fallbackAgentName);
		case 'rovo_chat':
			return createTypedFallbackAgent(identifier, agentNames.rovo, 'rovo');
		default:
			if (!fg('platform_move_presence_agents') && identifier.agentType === undefined) {
				return resolveStudioAgentById(identifier, assistanceService, fallbackAgentName);
			}

			return createTypedFallbackAgent(identifier, fallbackAgentName);
	}
};

export const useAgentsByIdsWithAgentEdits: (
	props: Omit<UseAgentIdentifiersWithAgentEditsProps, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => Map<string, AgentWithPresence> | null = createHookWithAssistanceService(
	({
		agentIdentifiers,
		fallbackAgentName,
		agentNames,
		assistanceServiceInjectedParams: { assistanceService },
	}: UseAgentIdentifiersWithAgentEditsProps) => {
		const { data } = useQuery({
			queryKey: [
				'agentPresenceAgentsByIds',
				fg('confluence_agent_presence_profile_by_type') ? 'type-directed' : 'legacy',
				...agentIdentifiers.map(({ agentId, agentType }) => `${agentType ?? ''}:${agentId}`).sort(),
			],
			queryFn: async () => {
				if (!fg('confluence_agent_presence_profile_by_type')) {
					const entries = await Promise.all(
						agentIdentifiers.map(async ({ agentId }): Promise<[string, AgentWithPresence]> => {
							try {
								return [agentId, await assistanceService.getAgentDetails(agentId)];
							} catch {
								try {
									return [
										agentId,
										await assistanceService.getAgentDetailsByIdentityAccountId(agentId),
									];
								} catch {
									return [agentId, createFallbackAgent(agentId, fallbackAgentName)];
								}
							}
						}),
					);

					return new Map(entries);
				}

				const identityAgentIds = agentIdentifiers.flatMap((identifier) =>
					hasConcreteAgentId(identifier) && identifier.agentType === 'mcp'
						? [identifier.agentId]
						: [],
				);
				const identityUsersByIdPromise: Promise<Map<string, IdentityUser>> =
					identityAgentIds.length > 0
						? fetchPresenceAGGUsers(identityAgentIds)
								.then((users) => new Map(users.map((user) => [user.userId, user])))
								.catch(() => new Map())
						: Promise.resolve(new Map());

				return new Map(
					await Promise.all(
						agentIdentifiers.map(
							async (identifier) =>
								[
									identifier.agentId,
									await resolveAgentIdentifier(
										identifier,
										assistanceService,
										identityUsersByIdPromise,
										fallbackAgentName,
										agentNames,
									),
								] as const,
						),
					),
				);
			},
			enabled: agentIdentifiers.length > 0,
		});

		return data ?? null;
	},
);
